/**
 * Route helper functions for generating type-safe URLs
 * This file only exports route helper functions, not the router itself.
 * The router is now handled by React Router framework mode in app/routes/
 */

/**
 * Type-safe route definitions
 * Static routes are defined as string constants
 * Dynamic routes are defined as functions that take parameters
 */
type AppRoutes = {
  home: string;
  notFound: string;
  play: string;
  categories: string;
  dailyChallenge: string;
  endlessTrivia: string;
  scheduledQuizBase: string;
  editDailyChallenge: string;
  partnerPortal: string;
  helpCenter: string;
  contactUs: string;
  privacyPolicy: string;
  termsOfService: string;
  affiliateTerms: string;
  referralCreditTerms: string;
  profile: string;
  tag: (name: string) => string;
  quiz: (slug: string) => string;
  scheduledQuizEmbed: (embedKey: string) => string;
  board: (inviteSlug: string) => string;
  partnerHistoryPlayTrivia: string;
  partnerLandingHistory: string;
  partnerTag: (partnerName: string, tagName: string) => string;
  partnerLeaderboard: (partnerName: string) => string;
  partnerIndyWeek: string;
  partnerWral: string;
  getTrivia: string;
  funAnimalFacts: string;
  devAnswerCards: string;
  devBadges: string;
  devHome: string;
  devStreakRepairTest: string;
  signIn: string;
  signUp: string;
  knockout: string;
  htmlTrivia: string;
  forNewsletters: string;
  pricing: string;
  affiliates: string;
  affiliateAssets: string;
  subscriptionConfirmation: string;
  kitAuthorize: string;
  kitCallback: string;
  kitEdit: string;
  kitWelcome: string;
  developersApi: string;
  embedQuizGuide: string;
  addAtrivialToKitGuide: string;
  autoUpdatingEmailGuide: string;
  fixedEmailGuide: string;
  articles: string;
  newsletterQuizArticle: string;
  multiChannelQuizArticle: string;
  sponsorRevenueArticle: string;
  readerRetentionArticle: string;
  automatedContentArticle: string;
};

// Define routes with type safety
export const routes: AppRoutes = {
  home: '/',
  notFound: '/404',
  play: '/play',
  categories: '/categories',
  dailyChallenge: '/daily',
  endlessTrivia: '/endless-trivia',
  scheduledQuizBase: '/scheduled-quiz',
  editDailyChallenge: '/edit-daily-challenge',
  partnerPortal: '/portal',
  helpCenter: '/help',
  contactUs: '/contact-us',
  privacyPolicy: '/privacy-policy',
  termsOfService: '/terms-of-service',
  affiliateTerms: '/affiliates/terms',
  referralCreditTerms: '/referrals/terms',
  profile: '/profile',
  tag: (name: string) => `/${encodeURIComponent(name)}`,
  quiz: (slug: string) => `/quiz/${encodeURIComponent(slug)}`,
  scheduledQuizEmbed: (embedKey: string) => `/scheduled-quiz/${encodeURIComponent(embedKey)}`,
  board: (inviteSlug: string) => `/boards/${encodeURIComponent(inviteSlug)}`,
  partnerHistoryPlayTrivia: '/partner/history/play-trivia',
  partnerLandingHistory: '/partner/history',
  partnerTag: (partnerName: string, tagName: string) => `/partner/${partnerName}/${tagName}`,
  partnerLeaderboard: (partnerName: string) => `/partner/${partnerName}/leaderboard`,
  partnerIndyWeek: '/partner/indyweek',
  partnerWral: '/partner/wral',
  getTrivia: '/get-trivia',
  funAnimalFacts: '/fun-animal-facts',
  devAnswerCards: '/dev/answer-cards',
  devBadges: '/dev/badges',
  devHome: '/dev/home',
  devStreakRepairTest: '/dev/streak-repair-test',
  signIn: '/sign-in',
  signUp: '/sign-up',
  knockout: '/knockout',
  htmlTrivia: '/html-trivia',
  forNewsletters: '/for-newsletters',
  pricing: '/pricing',
  affiliates: '/affiliates',
  affiliateAssets: '/affiliates/assets',
  subscriptionConfirmation: '/subscription-confirmation',
  kitAuthorize: '/integrations/kit/authorize',
  kitCallback: '/integrations/kit/callback',
  kitEdit: '/integrations/kit/edit',
  kitWelcome: '/integrations/kit/welcome',
  developersApi: '/developers/api',
  embedQuizGuide: '/guides/embed-a-quiz-on-your-website',
  addAtrivialToKitGuide: '/guides/add-atrivial-to-kit',
  autoUpdatingEmailGuide: '/guides/add-auto-updating-quiz-to-email',
  fixedEmailGuide: '/guides/add-quiz-to-email',
  articles: '/articles',
  newsletterQuizArticle: '/articles/turn-newsletter-content-into-quizzes-and-polls',
  multiChannelQuizArticle: '/articles/publish-one-quiz-across-email-web-and-links',
  sponsorRevenueArticle: '/articles/new-sponsor-slot-to-increase-revenue',
  readerRetentionArticle: '/articles/retain-readers-with-gamification',
  automatedContentArticle: '/articles/automate-content-creation',
};

/** True when the pathname is the partner portal route (with or without trailing slash). */
export function isPartnerPortalPathname(pathname: string): boolean {
  return pathname === routes.partnerPortal || pathname.startsWith(`${routes.partnerPortal}/`);
}

/** True when the pathname is a Kit App install or editor route. */
export function isKitAuthorizePathname(pathname: string): boolean {
  return pathname === routes.kitAuthorize || pathname === `${routes.kitAuthorize}/`;
}

export function isKitEditPathname(pathname: string): boolean {
  return pathname === routes.kitEdit || pathname === `${routes.kitEdit}/`;
}

export function isKitWelcomePathname(pathname: string): boolean {
  return pathname === routes.kitWelcome || pathname === `${routes.kitWelcome}/`;
}

/** True on /pricing (trailing-slash variants redirect to the canonical no-slash URL). */
export function isPricingPathname(pathname: string): boolean {
  return pathname === routes.pricing || pathname === `${routes.pricing}/`;
}

/** True on legacy /for-newsletters (nginx + route redirect permanently to /). */
export function isForNewslettersPathname(pathname: string): boolean {
  return pathname === routes.forNewsletters || pathname === `${routes.forNewsletters}/`;
}

export function isAffiliateAssetsPathname(pathname: string): boolean {
  return pathname === routes.affiliateAssets || pathname === `${routes.affiliateAssets}/`;
}

/** True on /articles and every article under it. */
export function isArticlesPathname(pathname: string): boolean {
  return pathname === routes.articles || pathname.startsWith(`${routes.articles}/`);
}

/** True for newsletter acquisition surfaces that share the marketing route treatment. */
export function isNewsletterMarketingPathname(pathname: string): boolean {
  return (
    pathname === routes.home ||
    (import.meta.env.DEV &&
      (pathname === '/dev/headline-options' || pathname === '/dev/headline-options/')) ||
    isForNewslettersPathname(pathname) ||
    isAffiliateAssetsPathname(pathname) ||
    isArticlesPathname(pathname) ||
    pathname === routes.affiliates ||
    pathname === `${routes.affiliates}/`
  );
}

/** True on /developers/api (trailing-slash variants redirect to the canonical no-slash URL). */
export function isDevelopersApiPathname(pathname: string): boolean {
  return pathname === routes.developersApi || pathname === `${routes.developersApi}/`;
}

/** True on /help (trailing-slash variants redirect to the canonical no-slash URL). */
export function isHelpCenterPathname(pathname: string): boolean {
  return pathname === routes.helpCenter || pathname === `${routes.helpCenter}/`;
}

/** True on /help or /guides/* paths. */
export function isHelpOrGuidePathname(pathname: string): boolean {
  return (
    isHelpCenterPathname(pathname) ||
    pathname.startsWith('/guides/') ||
    pathname === routes.embedQuizGuide ||
    pathname === routes.addAtrivialToKitGuide
  );
}

export type AppRouteHandle = {
  showXPTracker?: boolean;
  logoDestination?: string;
};

export const playerRouteHandle = {
  logoDestination: routes.play,
} satisfies AppRouteHandle;

export const xpTrackerRouteHandle = {
  ...playerRouteHandle,
  showXPTracker: true,
} satisfies AppRouteHandle;

export function hasXPTrackerRouteHandle(matches: Array<{ handle?: unknown }>): boolean {
  return matches.some(
    (match) => (match.handle as AppRouteHandle | undefined)?.showXPTracker === true
  );
}

export function getLogoDestinationFromRouteHandles(matches: Array<{ handle?: unknown }>): string {
  for (const match of matches) {
    const destination = (match.handle as AppRouteHandle | undefined)?.logoDestination;
    if (destination) return destination;
  }
  return routes.home;
}

// Router is now handled by React Router framework mode
// This file only exports route helper functions for URL generation
